/**
 * ItemForm Component — "Add an item" editor (Paiseh A.dc design refresh)
 * Form for creating/editing items
 */

import { useState } from 'react'
import { ItemFormData, Category } from '@/types'

interface ItemFormProps {
  initialData?: Partial<ItemFormData>
  categories?: Category[]
  onSubmit: (data: ItemFormData) => Promise<void>
  onCancel: () => void
  submitLabel?: string
}

const inputClass =
  'w-full rounded-[12px] border border-ink/[.15] px-3.5 py-3 text-[15px] text-ink placeholder:text-paiseh-faint focus:outline-none focus:border-paiseh-orange focus:ring-2 focus:ring-paiseh-peach transition-shadow'
const labelClass = 'block text-[13px] font-bold text-paiseh-muted mb-1.5'

// Design labels mapped onto the real low/medium/high priority values
const PRIORITY_OPTIONS: Array<{ value: 'low' | 'medium' | 'high'; label: string }> = [
  { value: 'low', label: 'Nice to have' },
  { value: 'medium', label: 'Would love' },
  { value: 'high', label: 'Dying for it' },
]

export const ItemForm = ({
  initialData,
  categories = [],
  onSubmit,
  onCancel,
  submitLabel = 'Add to wishlist',
}: ItemFormProps) => {
  const [formData, setFormData] = useState<ItemFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    url: initialData?.url || '',
    imageUrl: initialData?.imageUrl || '',
    price: initialData?.price,
    currency: initialData?.currency || 'SGD',
    categoryId: initialData?.categoryId,
    priority: initialData?.priority || 'medium',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const cleanData = {
        ...formData,
        url: formData.url || undefined,
        imageUrl: formData.imageUrl || undefined,
        categoryId: formData.categoryId || undefined,
      }
      await onSubmit(cleanData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 font-jakarta text-ink">
      {/* Product link */}
      <div>
        <label htmlFor="url" className={labelClass}>
          Product link
        </label>
        <div className="flex items-center gap-2.5 bg-[#FDFBF7] border border-ink/[.12] rounded-[14px] px-3.5 py-3 focus-within:border-paiseh-orange">
          <span className="text-base">🔗</span>
          <input
            id="url"
            type="url"
            maxLength={500}
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            placeholder="Paste a link from any store…"
            className="flex-1 bg-transparent text-[14.5px] focus:outline-none placeholder:text-paiseh-faint"
          />
        </div>
      </div>

      {/* Preview row: photo + core details */}
      <div className="flex gap-[18px]">
        <div className="w-[110px] flex-none">
          <div
            className="h-[110px] rounded-[16px] overflow-hidden bg-cover bg-center flex items-end justify-center"
            style={{
              background: formData.imageUrl
                ? `center/cover no-repeat url(${formData.imageUrl})`
                : 'repeating-linear-gradient(135deg,#F4E7D6,#F4E7D6 9px,#EFDFC9 9px,#EFDFC9 18px)',
            }}
          />
          <input
            id="imageUrl"
            type="url"
            maxLength={500}
            value={formData.imageUrl}
            onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
            placeholder="Image URL"
            className="mt-2 w-full rounded-[10px] border border-ink/[.12] px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-paiseh-orange"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="title" className={labelClass}>
            Item name
          </label>
          <input
            id="title"
            type="text"
            required
            maxLength={200}
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="What do you want?"
            className={inputClass}
          />
          <div className="grid grid-cols-[1fr_1.3fr] gap-3 mt-3.5">
            <div>
              <label htmlFor="price" className={labelClass}>
                Price
              </label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price ? formData.price / 100 : ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    price: e.target.value ? Math.round(parseFloat(e.target.value) * 100) : undefined,
                  })
                }
                placeholder="0.00"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="currency" className={labelClass}>
                Currency
              </label>
              <select
                id="currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className={inputClass}
              >
                <option value="SGD">SGD</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Category */}
      {categories.length > 0 && (
        <div>
          <label htmlFor="categoryId" className={labelClass}>
            Category
          </label>
          <select
            id="categoryId"
            value={formData.categoryId || ''}
            onChange={(e) => setFormData({ ...formData, categoryId: e.target.value || undefined })}
            className={inputClass}
          >
            <option value="">Uncategorized</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Priority — "how much you want it" */}
      <div>
        <label className={labelClass}>How much you want it</label>
        <div className="flex gap-2">
          {PRIORITY_OPTIONS.map(({ value, label }) => {
            const active = formData.priority === value
            return (
              <button
                key={value}
                type="button"
                onClick={() => setFormData({ ...formData, priority: value })}
                className={`flex-1 text-center text-[13.5px] font-bold py-2.5 rounded-full transition-colors ${
                  active
                    ? 'bg-paiseh-peach text-paiseh-orangeDark'
                    : 'bg-[#F5EFE5] text-[#8A7E72] hover:bg-paiseh-peach/60'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Note for gifters */}
      <div>
        <label htmlFor="description" className={labelClass}>
          Note for gifters <span className="text-[#B0A392] font-semibold">(optional)</span>
        </label>
        <textarea
          id="description"
          rows={2}
          maxLength={1000}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Cream or black please — not the pink one 🙏"
          className={`${inputClass} resize-none`}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-bold text-[#8A7E72] hover:text-ink transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-paiseh-orange text-white font-bold text-[14.5px] px-6 py-3 rounded-full shadow-[0_8px_18px_rgba(240,98,62,.3)] hover:opacity-95 disabled:opacity-50 transition-opacity"
        >
          {isSubmitting && (
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-hidden="true" />
          )}
          {submitLabel}
        </button>
      </div>
    </form>
  )
}
